import Vue from 'vue';
import Router from 'vue-router';
import { isStandaloneApp } from '@/utils/standaloneApp';
import marketRoutes from './routes/market';
import standaloneAppRoutes from './routes/standaloneApp';

Vue.use(Router);

// Keep track of popstate events (back/forward browser buttons)
let isBackNavigation = false;
window.addEventListener('popstate', () => {
  isBackNavigation = true;
  // Reset after a short delay
  setTimeout(() => {
    isBackNavigation = false;
  }, 100);
});

function getScrollBehavior() {
  try {
    const content = document.getElementById('content');
    const padding = parseFloat(window.getComputedStyle(content, null).getPropertyValue('padding-top'));

    if (content.getBoundingClientRect().top + padding < 0) {
      return { y: 0 };
    }
  } catch {
    // empty
  }

  return {};
}

function createRoutes(isStandalone) {
  if (isStandalone) {
    return standaloneAppRoutes;
  }

  return marketRoutes;
}

function createRouter() {
  const routes = createRoutes(isStandaloneApp(window.location.host));
  const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior(to, from, savedPosition) {
      // If the route has a hash, scroll to that element
      if (to.hash) {
        return { selector: to.hash };
      }

      // If we're using browser back/forward buttons and have a saved position, use that
      if (savedPosition) {
        return savedPosition;
      }

      // Check if we're navigating back to a page with a saved scroll position
      // This handles both router.back() and our custom back navigation
      const storedPosition = Vue.store?.getters['router/getScrollPosition']?.(to.fullPath);
      const isBackFromStore = Vue.store?.getters['router/isBackNavigation'];

      if ((isBackNavigation || router.isBackNavigation || isBackFromStore) && storedPosition) {
        return storedPosition;
      }

      // For all other navigation (new page, direct link, etc.), scroll to top
      return { x: 0, y: 0 };
    },
    routes,
  });

  // Add property to track back navigation
  router.isBackNavigation = false;

  // Patch the back() method to track when it's used
  const originalBack = router.back;
  router.back = function () {
    router.isBackNavigation = true;
    originalBack.apply(router, arguments);
    // Reset the flag after a short delay
    setTimeout(() => {
      router.isBackNavigation = false;
    }, 100);
  };

  router.beforeEach(async (to, from, next) => {
    // Save current scroll position before leaving the page
    if (from.name) {
      Vue.store.dispatch('router/saveCurrentScrollPosition', from);
    }

    Vue.store.commit('router/pushToHistory', from);

    if (!Vue.store.state.isDataFetched) {
      await Vue.store.dispatch('fetchData');
    }

    const toProfile = to.matched[0].path === '/my';
    const { authRequired } = to.meta;

    if (!Vue.store.getters.isDeviceLinked && toProfile && authRequired) {
      next({ path: '/my/error' });
    } else {
      next();
    }
  });

  // Add afterEach hook to ensure scroll is reset after navigation completes
  router.afterEach((to, from) => {
    // Handle nested routes - if the first matched route segment has changed
    // then we should scroll to top, even if it's a nested route change
    const toMainPath = to.matched[0]?.path;
    const fromMainPath = from.matched[0]?.path;
    const isBackFromStore = Vue.store?.getters['router/isBackNavigation'];

    // Only force scroll to top if this is NOT a back navigation
    // and we're navigating to a new main path or a completely new route
    if ((!isBackNavigation && !router.isBackNavigation && !isBackFromStore) &&
      (toMainPath !== fromMainPath || !from.name)) {
      // Use nextTick to ensure this happens after the DOM has updated
      Vue.nextTick(() => {
        window.scrollTo(0, 0);
      });
    }
  });

  return router;
}

export default createRouter;
